import {
  buildAskZghartaReferenceIndex,
  isAskZghartaCatalogItemAllowed,
  planAskZghartaFromCatalog,
  validateAskZghartaPlan,
} from './askZghartaPlanner';

export const DEFAULT_ASK_ZGHARTA_TIMEOUT_MS = 8000;

export const ASK_ZGHARTA_MAX_CONVERSATION_TURNS = 8;
export const ASK_ZGHARTA_MAX_USER_TURN_CHARS = 500;
export const ASK_ZGHARTA_MAX_TURN_SELECTIONS = 4;

export const ASK_ZGHARTA_SELECTION_TYPES = new Set(['place', 'business', 'event']);

const normalizeConversationTurn = turn => {
  if (!turn || typeof turn !== 'object') return null;
  if (turn.role === 'user') {
    if (typeof turn.content !== 'string' || !turn.content.trim()) return null;
    return { role: 'user', content: turn.content.slice(0, ASK_ZGHARTA_MAX_USER_TURN_CHARS) };
  }
  if (turn.role === 'assistant') {
    if (!Array.isArray(turn.selections)) return null;
    const selections = turn.selections
      .filter(sel => (
        sel &&
        typeof sel === 'object' &&
        ASK_ZGHARTA_SELECTION_TYPES.has(sel.type) &&
        typeof sel.id === 'string' &&
        sel.id.length > 0
      ))
      .map(sel => ({ type: sel.type, id: sel.id }))
      .slice(0, ASK_ZGHARTA_MAX_TURN_SELECTIONS);
    return { role: 'assistant', selections };
  }
  return null;
};

// Bounds a conversation history to the wire contract: invalid turns dropped,
// newest MAX_TURNS kept (oldest trimmed first), user content capped, and any
// leading assistant turns dropped post-trim — the Anthropic Messages API
// requires the first message turn to be role `user`.
export const boundAskZghartaMessages = messages => {
  if (!Array.isArray(messages)) return [];
  const turns = messages
    .map(normalizeConversationTurn)
    .filter(Boolean)
    .slice(-ASK_ZGHARTA_MAX_CONVERSATION_TURNS);
  const firstUserIndex = turns.findIndex(turn => turn.role === 'user');
  return firstUserIndex === -1 ? [] : turns.slice(firstUserIndex);
};

const configuredTimeout = Number(process.env.REACT_APP_ASK_ZGHARTA_PLANNER_TIMEOUT_MS || DEFAULT_ASK_ZGHARTA_TIMEOUT_MS);

export const askZghartaPlannerConfig = {
  remoteEnabled: Boolean(process.env.REACT_APP_ASK_ZGHARTA_PLANNER_URL),
  remoteEndpoint: process.env.REACT_APP_ASK_ZGHARTA_PLANNER_URL || '',
  timeoutMs: Number.isFinite(configuredTimeout) && configuredTimeout > 0 ? configuredTimeout : DEFAULT_ASK_ZGHARTA_TIMEOUT_MS,
};

const hasCoordinates = coords => (
  coords?.lat != null &&
  coords?.lng != null &&
  Number.isFinite(coords.lat) &&
  Number.isFinite(coords.lng)
);

const buildRemoteCatalogItems = (catalog = {}) => [
  ...(catalog.places || []).filter(isAskZghartaCatalogItemAllowed).map(place => ({
    id: String(place.id),
    type: 'place',
    name: place.name || '',
    nameAr: place.nameAr || null,
    category: place.category || '',
    village: place.village || null,
    villageAr: place.villageAr || null,
    description: place.description || null,
    descriptionAr: place.descriptionAr || null,
    featured: Boolean(place.featured),
    openHours: place.openHours || null,
    coordinates: hasCoordinates(place.coordinates)
      ? { lat: place.coordinates.lat, lng: place.coordinates.lng }
      : undefined,
    ...(place.publicDescriptionPendingFirstPartyConfirmation
      ? { publicDescriptionPendingFirstPartyConfirmation: true }
      : {}),
  })),
  ...(catalog.businesses || []).filter(isAskZghartaCatalogItemAllowed).map(business => ({
    id: String(business.id),
    type: 'business',
    name: business.name || '',
    nameAr: business.nameAr || null,
    category: business.category || '',
    village: business.village || null,
    villageAr: business.villageAr || null,
    description: business.description || null,
    descriptionAr: business.descriptionAr || null,
    coordinates: hasCoordinates(business.coordinates)
      ? { lat: business.coordinates.lat, lng: business.coordinates.lng }
      : undefined,
    rating: business.rating ?? null,
    verified: Boolean(business.verified),
    openHours: business.openHours || null,
    ...(business.publicDescriptionPendingFirstPartyConfirmation
      ? { publicDescriptionPendingFirstPartyConfirmation: true }
      : {}),
  })),
  ...(catalog.events || []).filter(isAskZghartaCatalogItemAllowed).map(event => ({
    id: String(event.id),
    type: 'event',
    name: event.name || '',
    nameAr: event.nameAr || null,
    category: event.category || '',
    village: event.village || null,
    description: event.description || null,
    descriptionAr: event.descriptionAr || null,
    date: event.date || null,
    time: event.time || null,
    featured: Boolean(event.featured),
  })),
];

export const buildAskZghartaRemotePlannerRequest = (catalog, request, messages) => {
  const boundedMessages = boundAskZghartaMessages(messages);
  return {
    schemaVersion: 1,
    request,
    catalog: {
      items: buildRemoteCatalogItems(catalog),
    },
    // Top-level sibling of `request` by contract: the composer echoes the
    // normalized request verbatim into plan.request, so messages must never
    // ride inside it. Absent/empty messages keep the payload byte-identical.
    ...(boundedMessages.length ? { messages: boundedMessages } : {}),
  };
};

const fallbackResult = (catalog, request, now, code, message) => ({
  plan: planAskZghartaFromCatalog({ catalog, request, now }),
  source: code ? 'fallback' : 'deterministic',
  remoteError: code && message ? { code, message } : undefined,
});

const parseRemotePlan = body => {
  if (!body || typeof body !== 'object') return null;
  if (body.plan) return body.plan;
  return body.schemaVersion === 1 ? body : null;
};

const createTimeoutSignal = timeoutMs => {
  if (typeof AbortController === 'undefined') return { signal: undefined, clear: () => {} };
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  return {
    signal: controller.signal,
    clear: () => clearTimeout(timeout),
  };
};

export async function planAskZgharta({
  catalog = {},
  request,
  messages,
  remoteConfig = askZghartaPlannerConfig,
  now = new Date(),
  fetchImpl = typeof fetch === 'function' ? fetch : null,
} = {}) {
  if (!remoteConfig?.remoteEnabled || !remoteConfig.remoteEndpoint || !fetchImpl) {
    return fallbackResult(catalog, request, now);
  }

  const timeout = createTimeoutSignal(remoteConfig.timeoutMs || DEFAULT_ASK_ZGHARTA_TIMEOUT_MS);

  try {
    const response = await fetchImpl(remoteConfig.remoteEndpoint, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(buildAskZghartaRemotePlannerRequest(catalog, request, messages)),
      signal: timeout.signal,
    });

    if (!response.ok) {
      return fallbackResult(catalog, request, now, 'http', `Remote planner returned ${response.status}`);
    }

    const body = await response.json();
    const plan = parseRemotePlan(body);
    if (!plan) {
      return fallbackResult(catalog, request, now, 'malformed_response', 'Remote planner response did not include a plan.');
    }

    const validation = validateAskZghartaPlan(plan, buildAskZghartaReferenceIndex(catalog));
    if (!validation.ok) {
      return fallbackResult(catalog, request, now, 'invalid_plan', validation.errors.join('; '));
    }

    return { plan, source: 'remote' };
  } catch (error) {
    const aborted = Boolean(timeout.signal?.aborted);
    return fallbackResult(
      catalog,
      request,
      now,
      aborted ? 'timeout' : 'network',
      error instanceof Error ? error.message : 'Remote planner request failed.',
    );
  } finally {
    timeout.clear();
  }
}
